import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, RefreshCw, AlertTriangle } from 'lucide-react';

export default function CameraModal({ onCapture, onClose }) {
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
    const [isPermDenied, setIsPermDenied] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const startCamera = async () => {
        setError(null);
        setIsPermDenied(false);
        try {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: {
                    facingMode: facingMode
                },
                audio: false
            };

            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(newStream);
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }
        } catch (err) {
            console.error("Camera Error:", err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError("Camera blocked.");
                setIsPermDenied(true);
            } else {
                setError("Camera unavailable: " + (err.message || "Unknown error"));
            }
        }
    };

    useEffect(() => {
        startCamera();
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [facingMode, retryCount]);

    const handleCapture = () => {
        if (!videoRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');

        // Flip if user facing
        if (facingMode === 'user') {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
        }

        ctx.drawImage(videoRef.current, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `camera_capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
                onCapture(file);
            }
        }, 'image/jpeg', 0.9);
    };

    const toggleCamera = () => {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    };

    // Cleanup on unmount (redundant with useEffect but fail-safe)
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        }
    }, [stream]);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    return createPortal(
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 100000,
            background: 'black',
            display: 'flex', flexDirection: 'column'
        }}>
            {/* Header */}
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10 }}>
                <span style={{ color: 'white', fontWeight: 'bold' }}>Take Photo</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                    <X size={24} />
                </button>
            </div>

            {/* Viewport */}
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#000' }}>
                {error ? (
                    <div style={{ color: 'white', textAlign: 'center', padding: '30px', maxWidth: '400px' }}>
                        <div style={{ marginBottom: '20px', display: 'inline-flex', padding: '15px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '50%' }}>
                            <AlertTriangle size={48} color="#ef4444" />
                        </div>
                        <h3 style={{ marginBottom: '10px', fontSize: '18px' }}>{error}</h3>

                        {isPermDenied && (
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '12px', marginTop: '20px', textAlign: 'left', fontSize: '14px', lineHeight: '1.6' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#fbbf24' }}>How to Enable:</div>
                                {isIOS ? (
                                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                        <li>Open <b>iPhone Settings</b> ⚙️.</li>
                                        <li>Tap your Browser (Chrome/Safari).</li>
                                        <li>Turn <b>Camera</b> switch <b>ON</b>.</li>
                                        <li>Tap <b>Retry Access</b> below.</li>
                                    </ul>
                                ) : (
                                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                        <li>Tap the 🔒 <b>Lock icon</b> in the address bar.</li>
                                        <li>Tap <b>Permissions</b> (or Site Settings).</li>
                                        <li><b>Allow</b> Camera access.</li>
                                    </ul>
                                )}
                            </div>
                        )}

                        <button
                            onClick={() => setRetryCount(c => c + 1)}
                            style={{ marginTop: '25px', padding: '10px 20px', borderRadius: '25px', border: 'none', background: 'var(--primary)', color: 'white', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', margin: '25px auto 0' }}
                        >
                            <RefreshCw size={18} /> Retry Access
                        </button>
                    </div>
                ) : (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                        }}
                    />
                )}
            </div>

            {/* Controls */}
            <div style={{ padding: '30px', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingBottom: 'calc(30px + env(safe-area-inset-bottom))' }}>
                <button onClick={toggleCamera} style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', border: 'none', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <RefreshCw size={24} />
                </button>

                <button
                    onClick={handleCapture}
                    disabled={!!error}
                    style={{
                        width: '80px', height: '80px',
                        borderRadius: '50%',
                        background: 'white',
                        border: '4px solid rgba(255,255,255,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: error ? 'not-allowed' : 'pointer',
                        opacity: error ? 0.5 : 1
                    }}
                >
                    <div style={{ width: '70px', height: '70px', borderRadius: '50%', border: '2px solid black' }} />
                </button>

                <div style={{ width: '50px' }} /> {/* Spacer to balance layout */}
            </div>
        </div>,
        document.body
    );
}
